/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { deploymentsCreateDeployment } from "../funcs/deploymentsCreateDeployment.js";
import { deploymentsGetDeployment } from "../funcs/deploymentsGetDeployment.js";
import { deploymentsListDeployments } from "../funcs/deploymentsListDeployments.js";
import { deploymentsPromoteDeployment } from "../funcs/deploymentsPromoteDeployment.js";
import { deploymentsRollbackDeployment } from "../funcs/deploymentsRollbackDeployment.js";
import { deploymentsStartDeployment } from "../funcs/deploymentsStartDeployment.js";
import { deploymentsStopDeployment } from "../funcs/deploymentsStopDeployment.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import * as components from "../models/components/index.js";
import * as operations from "../models/operations/index.js";
import { unwrapAsync } from "../types/fp.js";
import { PageIterator, unwrapResultIterator } from "../types/operations.js";

export class Deployments extends ClientSDK {
  /**
   * Create deployment
   *
   * @remarks
   * Create a deployment for an app in a project.
   *
   * Provide exactly one source:
   * - `image`: deploy a prebuilt Docker image as-is (no build).
   * - `git`: build and deploy from the app's connected GitHub repository, a branch, a specific commit, or a fork commit. Requires the app to have a repository connected.
   * - `deployment`: re-run an existing deployment by its id. Git-connected apps rebuild from the recorded commit; other apps reuse the recorded image.
   *
   * Returns immediately with a `deploymentId`. The build and rollout run asynchronously — poll `deployments.getDeployment` to watch status until it is ready.
   *
   * **Authentication**: requires a root key with permission to create deployments.
   *
   * @deprecated method: This will be removed in a future release, please migrate away from it as soon as possible.
   */
  async createDeployment(
    request: components.V1DeploymentsCreateDeploymentRequestBodyUnion,
    options?: RequestOptions,
  ): Promise<components.V1DeploymentsCreateDeploymentResponseBody> {
    return unwrapAsync(deploymentsCreateDeployment(
      this,
      request,
      options,
    ));
  }

  /**
   * Get deployment
   *
   * @remarks
   * Retrieve a single deployment by its id.
   *
   * Use this to check a deployment's status after creating it, or to inspect the
   * runtime configuration of an existing deployment.
   *
   * **Required Permissions**
   *
   * Your root key must have one of the following permissions:
   * - `environment.*.read_deployment` (to read deployments in any environment)
   * - `environment.<environment_id>.read_deployment` (to read deployments in a specific environment)
   */
  async getDeployment(
    request: components.V1DeploymentsGetDeploymentRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1DeploymentsGetDeploymentResponseBody> {
    return unwrapAsync(deploymentsGetDeployment(
      this,
      request,
      options,
    ));
  }

  /**
   * List deployments
   *
   * @remarks
   * Retrieve a paginated list of deployments within a workspace, newest first.
   *
   * Filter by project, app, environment, and lifecycle status. All filters are
   * optional; with none set, every deployment in the workspace is returned.
   * Filters nest: `app` requires `project`, and `environment` requires both
   * `project` and `app`. Results are paginated; when `hasMore` is true, pass the
   * returned `cursor` to fetch the next page.
   *
   * **Required Permissions**
   *
   * Your root key must have the `environment.*.read_deployment` permission.
   * Listing spans environments, so a grant on a single environment is not
   * sufficient.
   */
  async listDeployments(
    request: components.V1DeploymentsListDeploymentsRequestBody,
    options?: RequestOptions,
  ): Promise<
    PageIterator<
      operations.DeploymentsListDeploymentsResponse,
      { cursor: string }
    >
  > {
    return unwrapResultIterator(deploymentsListDeployments(
      this,
      request,
      options,
    ));
  }

  /**
   * Promote deployment
   *
   * @remarks
   * Promote a deployment to become the current deployment for its environment.
   * All sticky domains are reassigned from the current deployment to the
   * promoted one, and the previous deployment is scheduled for standby.
   *
   * The deployment must be ready, not already shutting down, belong to the
   * production environment, and its app must already have a current deployment.
   * Promoting the deployment that is already current fails, unless the app is in
   * a rolled-back state, in which case promoting the current deployment
   * confirms the rollback and re-enables automatic promotion of future
   * deployments.
   *
   * Promotion runs as a durable workflow: this endpoint returns once the
   * promotion is accepted. Poll `getDeployment` or `listDeployments` to observe
   * the result.
   *
   * **Required Permissions**
   *
   * Your root key must have one of the following permissions:
   * - `environment.*.promote_deployment` (to promote deployments in any environment)
   * - `environment.<environment_id>.promote_deployment` (to promote deployments in a specific environment)
   */
  async promoteDeployment(
    request: components.V1DeploymentsPromoteDeploymentRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1DeploymentsPromoteDeploymentResponseBody> {
    return unwrapAsync(deploymentsPromoteDeployment(
      this,
      request,
      options,
    ));
  }

  /**
   * Rollback deployment
   *
   * @remarks
   * Roll live traffic back to a previous deployment. `deploymentId` is the
   * deployment to roll back TO; the app's current deployment is used as
   * the rollback source automatically.
   *
   * The target deployment must be ready, not already shutting down, belong to
   * the production environment, and must not itself be the current deployment.
   *
   * After a rollback the app is marked as rolled back, which prevents new
   * deployments from automatically taking over live traffic. Promote the
   * rolled-back deployment (or a newer one) to clear this state.
   *
   * Rollback runs as a durable workflow: this endpoint returns once the
   * rollback is accepted. Poll `getDeployment` or `listDeployments` to observe
   * the result.
   *
   * **Required Permissions**
   *
   * Your root key must have one of the following permissions:
   * - `environment.*.rollback_deployment` (to roll back deployments in any environment)
   * - `environment.<environment_id>.rollback_deployment` (to roll back deployments in a specific environment)
   */
  async rollbackDeployment(
    request: components.V1DeploymentsRollbackDeploymentRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1DeploymentsRollbackDeploymentResponseBody> {
    return unwrapAsync(deploymentsRollbackDeployment(
      this,
      request,
      options,
    ));
  }

  /**
   * Start deployment
   *
   * @remarks
   * Start a deployment that was previously stopped with `stopDeployment`, so it
   * serves traffic again. The deployment keeps the configuration it had when it
   * was stopped; nothing is rebuilt or redeployed.
   *
   * The deployment must currently be stopped, and must belong to a
   * non-production environment. Production deployments are never stopped, so
   * they cannot be started.
   *
   * Starting is asynchronous: this endpoint only enqueues the start and returns
   * immediately. Poll `getDeployment` until the status reaches `ready`.
   *
   * **Required Permissions**
   *
   * Your root key must have one of the following permissions:
   * - `environment.*.start_deployment` (to start deployments in any environment)
   * - `environment.<environment_id>.start_deployment` (to start deployments in a specific environment)
   */
  async startDeployment(
    request: components.V1DeploymentsStartDeploymentRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1DeploymentsStartDeploymentResponseBody> {
    return unwrapAsync(deploymentsStartDeployment(
      this,
      request,
      options,
    ));
  }

  /**
   * Stop deployment
   *
   * @remarks
   * Stop a running preview deployment. Stopped deployments keep their
   * configuration and can be resumed later with `startDeployment`.
   *
   * The deployment must be ready and running, and must belong to a
   * non-production environment; production deployments cannot be stopped.
   * A deployment that is already draining from a previous stop is rejected
   * with a precondition error.
   *
   * Stopping is asynchronous: this endpoint only enqueues the stop and returns
   * immediately. Poll `getDeployment` until the status reaches `stopped`.
   *
   * **Required Permissions**
   *
   * Your root key must have one of the following permissions:
   * - `environment.*.stop_deployment` (to stop deployments in any environment)
   * - `environment.<environment_id>.stop_deployment` (to stop deployments in a specific environment)
   */
  async stopDeployment(
    request: components.V1DeploymentsStopDeploymentRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1DeploymentsStopDeploymentResponseBody> {
    return unwrapAsync(deploymentsStopDeployment(
      this,
      request,
      options,
    ));
  }
}
